<template>
  <loading-state v-if="loading && useLoader" :variant="simpleCard ? 'simple' : 'more-info'" />
  <v-card v-else class="cbm-card d-flex flex-column justify-start pt-5 pb-6 px-4 rounded-lg" :class="useTabs ? 'set-height-tabs' : 'set-height'">
    <div class="d-flex justify-space-between">
      <span class="cbm-card-title">{{ title }}</span>
      <mf-tooltip v-if="tooltip" max-width="250" top variant="secondary">
        <template #activator="{ on }">
          <mf-icon class="help-icon" dense color="cinza-l30" icon="info" />
        </template>
        {{ tooltip }}
      </mf-tooltip>

      <mf-button v-if="ctaText" label="Ver mais detalhes" color="marca-0-l40" text icon="arrow_forward" icon-position="right" @click="$emit('cta-click')" />
    </div>

    <div class="cbm-card-content">
      <blur-card v-if="showBlur" :blur-caption="blurCaption" :blur-icon="blurIcon" />

      <div v-if="useTabs">
        <mf-tabs-menu v-model="templateTab" :tabs="tabsMenuItems" fixed-tabs class="my-4" :show-tooltip-name="false" />
        <div class="period-carousel d-flex flex-row justify-space-between align-center">
          <mf-button
            type="icon"
            icon="arrow_left"
            color="cinza-0"
            :disabled="disabledPrevious"
            @click="$emit('set-current-date', { activeDate, templateTab, type: 'previous' })"
          />
          <span class="text-cinza-d10" style="text-transform: capitalize">{{ formatActiveDate }}</span>
          <mt-button
            type="icon"
            color="cinza-0"
            icon="arrow_right"
            :disabled="disabledNext"
            @click="$emit('set-current-date', { activeDate, templateTab, type: 'next' })"
          />
        </div>
      </div>
      <div v-if="!useLoader" class="d-flex justify-center pt-2">
        <v-progress-circular indeterminate color="cinza-0" :size="60" />
      </div>
      <div v-else-if="numberText || numberText === 0" :class="`d-flex align-center py-${numberText ? 3 : 2}`">
        <span v-if="isCurrency" class="cbm-card-number description" :style="numberColor ? { color: numberColor } : {}">R$</span>
        <span class="cbm-card-number" :style="numberColor ? { color: numberColor } : {}">{{ numberText }}</span>
        <span v-if="numberDescription.length" class="cbm-card-number number-description description"> {{ numberDescription }}</span>
        <span v-if="isPercent" class="cbm-card-number description" :style="numberColor ? { color: numberColor } : {}">%</span>
        <mf-quick-actions
          v-if="quickAction"
          :quick-action-group-id="quickAction.id"
          :segmentation-group="quickAction.segmentationGroup"
          :short-description="quickAction.shortDescription"
          :long-description="quickAction.longDescription"
          :tooltip="quickAction.tooltip"
        />
      </div>
      <div v-else-if="useNoDataText" class="no-data-text d-flex justify-center align-center">
        <span>Não há dados para esse período.</span>
      </div>
      <div class="cbm-percent d-flex flex-row" :class="!numberText && !useNoDataText && 'pt-3'">
        <slot name="custom-additional-value"></slot>
        <mf-tooltip v-if="percentageTooltip" max-width="280" bottom variant="secondary">
          <template #activator="{ on }">
            <span class="cbm-percent-text pr-1" :class="getPercentageClass">{{ percentText }}</span>
            <span class="cbm-percent-description">{{ percentDescription }}</span>
          </template>
          {{ percentageTooltip }}
        </mf-tooltip>
        <div v-else class="d-flex align-center">
          <span v-if="useLoader" class="cbm-percent-text" :class="getPercentageClass">{{ percentText }}</span>
          <span v-if="useLoader" class="cbm-percent-description">{{ percentDescription }}</span>
        </div>
      </div>
      <slot name="additional"></slot>
    </div>
  </v-card>
</template>

<script>
// import { mapGetters, mapState } from 'vuex'
import { DateTime } from 'luxon'
import BlurCard from './BlurCard.vue'
import LoadingState from '../modules/home/views/components/LoadingState.vue'

export default {
  name: 'CbmCard',
  components: {
    LoadingState,
    BlurCard,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    isCurrency: {
      type: Boolean,
      default: false,
    },
    isPercent: {
      type: Boolean,
      default: false,
    },
    blurCaption: {
      type: String,
      default: '',
    },
    blurIcon: {
      type: String,
    },
    numberText: {
      type: [String, Number],
      default: '',
    },
    numberDescription: {
      type: String,
      default: '',
    },
    numberColor: {
      type: String,
      default: '',
    },
    percentText: {
      type: [String, Number],
      default: '',
    },
    percentDescription: {
      type: String,
      default: '',
    },
    quickAction: {
      type: Object,
    },
    useTabs: {
      type: Boolean,
      default: false,
    },
    useNoDataText: {
      type: Boolean,
      default: false,
    },
    tabsMenuItems: {
      type: Array,
      default: () => [],
    },
    tooltip: {
      type: [String, Boolean],
      default: false,
    },
    percentageTooltip: {
      type: [String, Boolean],
      default: false,
    },
    loader: {
      type: [String, Array],
    },
    booleanLoader: {
      type: Boolean,
      default: false,
    },
    useLoader: {
      type: Boolean,
      default: true,
    },
    activeDate: {
      type: String,
      defautl: '',
    },
    disabledPrevious: {
      type: Boolean,
      default: false,
    },
    disabledNext: {
      type: Boolean,
      default: false,
    },
    simpleCard: {
      type: Boolean,
      default: true,
    },
    tabsTooltips: {
      type: Array,
      default: () => [],
    },
    unsetPercentageColors: {
      type: Boolean,
      default: false,
    },
    ctaText: {
      type: String,
      default: '',
    },
    showBlur: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({ templateTab: 0 }),
  computed: {
    formatActiveDate() {
      return DateTime.fromFormat(this.activeDate, 'yyyy-MM').toFormat('MMMM yyyy')
    },
    percentNumber() {
      return this.percentText ? Number(this.percentText.substring(0, this.percentText.length - 1).replaceAll(',', '.')) : 0
    },
    getPercentageClass() {
      if (this.unsetPercentageColors || this.percentNumber === 0) {
        return 'unset-color'
      }
      return this.percentNumber > 0 ? 'positive' : 'negative'
    },
    loading() {
      if (this.booleanLoader) {
        return this.booleanLoader
      }
      if (this.loader) {
        if (Array.isArray(this.loader)) {
          return this.$wait.is(['primeiro-registro', 'last-update', 'get-last-update', ...this.loader])
        }
        return this.$wait.is(['primeiro-registro', 'last-update', 'get-last-update', this.loader])
      }
      return false
    },
  },
  watch: {
    templateTab(newValue) {
      this.$emit('change-tab', newValue)
    },
  },
}
</script>

<style scoped lang="scss">
.cbm-card {
  border-radius: 8px !important;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.16) !important;
  height: 100%;
  .no-data-text {
    text-align: center;
    height: 100%;
    color: $cinza-l40;
  }
  .cbm-card-title {
    color: $cinza-d10;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0.22px;
  }
  .cbm-card-number {
    color: $cinza-0;
    font-size: 36px;
    font-weight: 400;
    line-height: 44px;
    letter-spacing: 0.36px;
  }
  .number-description {
    font-size: 24px;
  }
  .description {
    color: $cinza-l40;
  }
  .cbm-percent {
    line-height: 120%;
    .cbm-percent-text {
      font-size: 18px;
      font-weight: 700;
      padding-right: 2px;
    }
    .cbm-percent-description {
      color: $cinza-0;
      font-size: 14px;
      font-weight: 400;
      padding-left: 2px;
    }
  }
}

.cbm-card-content {
  position: relative;
  background-color: transparent;
}

.unset-color {
  color: $cinza-d10;
}
.positive {
  color: $verde-0;
}
.negative {
  color: $vermelho-0;
}
::v-deep .tabs-menu .tabs-menu__tab {
  font-size: 14px;
}
::v-deep .v-slide-group__prev {
  display: none !important;
}
::v-deep .v-slide-group__next {
  display: none !important;
}
::v-deep .v-slide-group__content {
  padding-left: 0 !important;
}
::v-deep .v-tab {
  padding: 0 !important;
}

.not-use-loader {
  height: 50px;
  width: 30%;
  :first-child {
    height: 100%;
    width: 100%;
  }
}
.tabs-menu-custom-tooltip {
  box-shadow: none !important;
  color: red !important;
}
</style>
